/**
 * Tenant Context for Multi-Tenant UI State
 *
 * Provides tenant-scoped context management for the React frontend.
 * Manages the current tenant, tenant switching (for global admins),
 * and tenant-scoped state throughout the application.
 *
 * @example
 * ```tsx
 * // Wrap your app
 * <TenantProvider>
 *   <App />
 * </TenantProvider>
 *
 * // Use in components
 * const { currentTenant, canSwitchTenants, switchTenant } = useTenant();
 *
 * // Check tenant access
 * if (isGlobalAdmin) {
 *   // Show tenant selector
 * }
 * ```
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  type ReactNode,
} from 'react';

// Import Role and useAuth from AuthContext for type consistency and user sync
import { type Role, useAuth } from './AuthContext';

// Re-export Role for convenience
export type { Role };

// =============================================================================
// Types
// =============================================================================

/**
 * Tenant information
 */
export interface Tenant {
  /** Unique tenant identifier */
  id: string;
  /** Display name of the tenant */
  name: string;
  /** URL-friendly slug */
  slug: string;
  /** Whether the tenant is active */
  active: boolean;
  /** Tenant-specific settings */
  settings?: Record<string, unknown>;
}

/**
 * User information for tenant context
 */
export interface TenantUser {
  /** User ID */
  id: string;
  /** User's role */
  role: Role;
  /** User's tenant ID (null for global admins without specific tenant) */
  tenantId: string | null;
  /** User's name for display */
  name?: string;
}

/**
 * State for the tenant context
 */
export interface TenantState {
  /** The current active tenant (null for global view) */
  currentTenant: Tenant | null;
  /** List of available tenants (for global admins) */
  availableTenants: Tenant[];
  /** The authenticated user */
  user: TenantUser | null;
  /** Loading state for tenant operations */
  isLoading: boolean;
  /** Error state */
  error: string | null;
}

/**
 * Actions available in the tenant context
 */
export interface TenantActions {
  /** Switch to a different tenant (global admin only) */
  switchTenant: (tenantId: string | null) => Promise<void>;
  /** Set the current user (called from auth context) */
  setUser: (user: TenantUser | null) => void;
  /** Set available tenants (for global admins) */
  setAvailableTenants: (tenants: Tenant[]) => void;
  /** Set the current tenant directly */
  setCurrentTenant: (tenant: Tenant | null) => void;
  /** Clear error state */
  clearError: () => void;
  /** Refresh tenant data */
  refreshTenants: () => Promise<void>;
}

/**
 * Computed properties for tenant context
 */
export interface TenantComputed {
  /** Whether the user is a global admin */
  isGlobalAdmin: boolean;
  /** Whether the user is a tenant admin */
  isTenantAdmin: boolean;
  /** Whether the user can switch between tenants */
  canSwitchTenants: boolean;
  /** Whether tenant context is initialized */
  isInitialized: boolean;
  /** The effective tenant ID for API queries */
  effectiveTenantId: string | null;
}

/**
 * Full tenant context value
 */
export interface TenantContextValue extends TenantState, TenantActions, TenantComputed {}

// =============================================================================
// Context
// =============================================================================

/**
 * React context for tenant state
 */
const TenantContext = createContext<TenantContextValue | undefined>(undefined);

// =============================================================================
// Provider
// =============================================================================

/**
 * Props for TenantProvider
 */
export interface TenantProviderProps {
  /** Child components */
  children: ReactNode;
  /** Optional function to fetch available tenants (for global admins) */
  fetchTenants?: () => Promise<Tenant[]>;
  /** Optional function to fetch a specific tenant by ID */
  fetchTenant?: (tenantId: string) => Promise<Tenant | null>;
  /** Initial tenant (if known) */
  initialTenant?: Tenant | null;
  /** Initial user (if known) */
  initialUser?: TenantUser | null;
}

/**
 * Provider component for tenant context
 *
 * Manages multi-tenant state and provides tenant-scoped operations
 * throughout the application.
 *
 * @example
 * ```tsx
 * const fetchTenants = async () => {
 *   const response = await api.get('/tenants');
 *   return response.data;
 * };
 *
 * <TenantProvider fetchTenants={fetchTenants}>
 *   <App />
 * </TenantProvider>
 * ```
 */
export function TenantProvider({
  children,
  fetchTenants,
  fetchTenant,
  initialTenant = null,
  initialUser = null,
}: TenantProviderProps): JSX.Element {
  // Get user from AuthContext
  const { user: authUser } = useAuth();

  // State
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(initialTenant);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [user, setUser] = useState<TenantUser | null>(initialUser);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync user from AuthContext to TenantContext
  useEffect(() => {
    if (authUser) {
      setUser({
        id: authUser.id,
        name: authUser.name,
        role: authUser.role,
        tenantId: authUser.tenantId,
      });
    } else {
      setUser(null);
      setCurrentTenant(null);
      setAvailableTenants([]);
    }
  }, [authUser]);

  // Computed values
  const isGlobalAdmin = useMemo(() => user?.role === 'GLOBAL_ADMIN', [user?.role]);
  const isTenantAdmin = useMemo(() => user?.role === 'TENANT_ADMIN', [user?.role]);
  const canSwitchTenants = useMemo(() => isGlobalAdmin, [isGlobalAdmin]);
  const isInitialized = useMemo(() => user !== null, [user]);

  // Effective tenant ID for API queries
  const effectiveTenantId = useMemo(() => {
    // If a tenant is explicitly selected, use it
    if (currentTenant?.id) {
      return currentTenant.id;
    }
    // For non-global admins, use their assigned tenant
    if (!isGlobalAdmin && user?.tenantId) {
      return user.tenantId;
    }
    // Global admins without selection see all (null)
    return null;
  }, [currentTenant?.id, isGlobalAdmin, user?.tenantId]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Refresh available tenants (for global admins)
  const refreshTenants = useCallback(async () => {
    if (!fetchTenants || !isGlobalAdmin) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const tenants = await fetchTenants();
      setAvailableTenants(tenants);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch tenants';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [fetchTenants, isGlobalAdmin]);

  // Switch tenant (global admin only)
  const switchTenant = useCallback(
    async (tenantId: string | null) => {
      if (!canSwitchTenants) {
        setError('Permission denied: cannot switch tenants');
        return;
      }

      // Switching to "all tenants" view
      if (tenantId === null) {
        setCurrentTenant(null);
        return;
      }

      // Check if tenant is in available list
      const tenant = availableTenants.find((t) => t.id === tenantId);
      if (tenant) {
        setCurrentTenant(tenant);
        return;
      }

      // Fetch tenant if not in list
      if (fetchTenant) {
        setIsLoading(true);
        setError(null);

        try {
          const fetchedTenant = await fetchTenant(tenantId);
          if (fetchedTenant) {
            setCurrentTenant(fetchedTenant);
            // Add to available list if not present
            setAvailableTenants((prev) => {
              if (prev.some((t) => t.id === tenantId)) {
                return prev;
              }
              return [...prev, fetchedTenant];
            });
          } else {
            setError(`Tenant not found: ${tenantId}`);
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to fetch tenant';
          setError(message);
        } finally {
          setIsLoading(false);
        }
      } else {
        setError(`Tenant not found: ${tenantId}`);
      }
    },
    [canSwitchTenants, availableTenants, fetchTenant]
  );

  // Handle user changes - auto-set tenant for non-global users
  const handleSetUser = useCallback(
    (newUser: TenantUser | null) => {
      setUser(newUser);

      // Clear tenant selection when user logs out
      if (!newUser) {
        setCurrentTenant(null);
        setAvailableTenants([]);
        return;
      }

      // For non-global admins, we'll need to fetch their tenant
      // This is handled by the parent component that calls setUser
      // after fetching user data with tenant info
      if (newUser.role !== 'GLOBAL_ADMIN' && newUser.tenantId && fetchTenant) {
        // Fetch and set the user's tenant
        fetchTenant(newUser.tenantId)
          .then((tenant) => {
            if (tenant) {
              setCurrentTenant(tenant);
            }
          })
          .catch(() => {
            // Tenant fetch failed - will be handled by error boundary or parent
          });
      }
    },
    [fetchTenant]
  );

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<TenantContextValue>(
    () => ({
      // State
      currentTenant,
      availableTenants,
      user,
      isLoading,
      error,
      // Actions
      switchTenant,
      setUser: handleSetUser,
      setAvailableTenants,
      setCurrentTenant,
      clearError,
      refreshTenants,
      // Computed
      isGlobalAdmin,
      isTenantAdmin,
      canSwitchTenants,
      isInitialized,
      effectiveTenantId,
    }),
    [
      currentTenant,
      availableTenants,
      user,
      isLoading,
      error,
      switchTenant,
      handleSetUser,
      clearError,
      refreshTenants,
      isGlobalAdmin,
      isTenantAdmin,
      canSwitchTenants,
      isInitialized,
      effectiveTenantId,
    ]
  );

  return <TenantContext.Provider value={contextValue}>{children}</TenantContext.Provider>;
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook to access tenant context
 *
 * @returns The tenant context value
 * @throws Error if used outside of TenantProvider
 *
 * @example
 * ```tsx
 * function BotList() {
 *   const { currentTenant, isGlobalAdmin, effectiveTenantId } = useTenant();
 *
 *   // Fetch bots scoped to current tenant
 *   const { data: bots } = useQuery(['bots', effectiveTenantId], () =>
 *     fetchBots(effectiveTenantId)
 *   );
 *
 *   return (
 *     <div>
 *       <h1>Bots {currentTenant ? `for ${currentTenant.name}` : '(All Tenants)'}</h1>
 *       {bots?.map(bot => <BotCard key={bot.id} bot={bot} />)}
 *     </div>
 *   );
 * }
 * ```
 */
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);

  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }

  return context;
}

// =============================================================================
// Utility Hooks
// =============================================================================

/**
 * Hook to check if the current user has a specific role or higher
 *
 * Role hierarchy: GLOBAL_ADMIN > TENANT_ADMIN > TENANT_USER > VIEWER
 *
 * @param minimumRole - The minimum role required
 * @returns Whether the user has the required role level
 *
 * @example
 * ```tsx
 * function AdminPanel() {
 *   const hasAdminAccess = useHasMinimumRole('TENANT_ADMIN');
 *
 *   if (!hasAdminAccess) {
 *     return <AccessDenied />;
 *   }
 *
 *   return <AdminContent />;
 * }
 * ```
 */
export function useHasMinimumRole(minimumRole: Role): boolean {
  const { user } = useTenant();

  return useMemo(() => {
    if (!user) return false;

    const roleHierarchy: Record<Role, number> = {
      GLOBAL_ADMIN: 4,
      TENANT_ADMIN: 3,
      TENANT_USER: 2,
      VIEWER: 1,
    };

    const userLevel = roleHierarchy[user.role];
    const requiredLevel = roleHierarchy[minimumRole];

    return userLevel >= requiredLevel;
  }, [user, minimumRole]);
}

/**
 * Hook to check if the current user has a specific role
 *
 * @param role - The role to check for
 * @returns Whether the user has the exact role
 *
 * @example
 * ```tsx
 * function TenantSelector() {
 *   const isGlobalAdmin = useHasRole('GLOBAL_ADMIN');
 *
 *   if (!isGlobalAdmin) {
 *     return null; // Only show for global admins
 *   }
 *
 *   return <TenantDropdown />;
 * }
 * ```
 */
export function useHasRole(role: Role): boolean {
  const { user } = useTenant();
  return user?.role === role;
}

/**
 * Hook to get the current tenant ID for API queries
 *
 * Returns the effective tenant ID based on:
 * 1. Explicitly selected tenant (for global admins)
 * 2. User's assigned tenant (for non-global users)
 * 3. null (for global admin "all tenants" view)
 *
 * @returns The tenant ID to use for scoped queries
 *
 * @example
 * ```tsx
 * function BotList() {
 *   const tenantId = useTenantId();
 *
 *   const { data: bots } = useQuery(
 *     ['bots', tenantId],
 *     () => fetchBots(tenantId ? { tenantId } : {})
 *   );
 *
 *   return <BotListView bots={bots} />;
 * }
 * ```
 */
export function useTenantId(): string | null {
  const { effectiveTenantId } = useTenant();
  return effectiveTenantId;
}

/**
 * Hook to check if tenant-scoped queries should include all tenants
 *
 * Returns true when:
 * - User is global admin AND
 * - No specific tenant is selected
 *
 * @returns Whether to query all tenants
 *
 * @example
 * ```tsx
 * function BotStats() {
 *   const includeAllTenants = useIncludeAllTenants();
 *
 *   const { data: stats } = useQuery(
 *     ['stats', includeAllTenants],
 *     () => fetchStats({ global: includeAllTenants })
 *   );
 *
 *   return <StatsView stats={stats} />;
 * }
 * ```
 */
export function useIncludeAllTenants(): boolean {
  const { isGlobalAdmin, currentTenant } = useTenant();
  return isGlobalAdmin && currentTenant === null;
}

// =============================================================================
// Error Class
// =============================================================================

/**
 * Error class for tenant access violations
 */
export class TenantAccessError extends Error {
  readonly code = 'TENANT_ACCESS_DENIED';

  constructor(message: string) {
    super(message);
    this.name = 'TenantAccessError';
    Object.setPrototypeOf(this, TenantAccessError.prototype);
  }
}

// Export the context for advanced usage (e.g., testing)
export { TenantContext };
export default TenantContext;
